import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Empty, ErrorBanner, formatUtc, useAsync } from '../../components/Async'
import { PageTitle } from '../../components/PageTitle'
import { ApiError, dictionaries } from '../../lib/api'

export function DictionaryListPage() {
  const [search, setSearch] = useState('')
  const list = useAsync(() => dictionaries.list({ search: search || undefined }), [search])
  const [name, setName] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const navigate = useNavigate()

  async function create() {
    setFailure(null)
    setBusy(true)
    try {
      // Named before it exists. This used to invent "Dictionary 2026-08-31 19:26:20" and create the row
      // on the click, which is how a tenant ended up holding two hundred near-empty dictionaries nobody
      // could remove. A name is one field and it is the only thing that makes the row findable later.
      const created = await dictionaries.create(name.trim())
      navigate(`/dictionaries/${created.dictionaryId}`)
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The dictionary could not be created.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="page-header">
        <div>
          <PageTitle>Dictionaries</PageTitle>
          <p>
            Detection rules, versioned on their own. A policy binds a dictionary by reference, so
            correcting a term here reaches every policy that carries it the next time each is published.
            Every tenant starts with the collections Coastline ships; they are yours to edit.
          </p>
        </div>
        <form
          className="toolbar"
          onSubmit={(event) => {
            event.preventDefault()
            void create()
          }}
        >
          <input
            aria-label="Name for a new dictionary"
            placeholder="Name a new dictionary"
            value={name}
            onChange={(event) => setName(event.target.value)}
            data-testid="new-dictionary-name"
          />
          <button type="submit" className="primary" disabled={busy || !name.trim()} data-testid="new-dictionary">
            Create
          </button>
        </form>
      </div>

      <ErrorBanner message={failure ?? list.error} />

      <div className="card">
        <div className="card-body toolbar">
          <input
            className="grow"
            type="search"
            placeholder="Search dictionaries"
            aria-label="Search dictionaries"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <button type="button" onClick={list.reload}>
            Refresh
          </button>
        </div>

        <div className="table-scroll">
          <table data-testid="dictionary-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Rules</th>
                <th>Published</th>
                <th className="secondary">Source</th>
                <th className="secondary">Created</th>
              </tr>
            </thead>
            <tbody>
              {list.value?.items.map((dictionary) => (
                <tr key={dictionary.dictionaryId}>
                  <td>
                    <Link to={`/dictionaries/${dictionary.dictionaryId}`}>{dictionary.name}</Link>
                    {dictionary.description && <div className="muted">{dictionary.description}</div>}
                  </td>
                  <td>
                    {dictionary.contentRuleCount} content
                    {dictionary.signalRuleCount > 0 && `, ${dictionary.signalRuleCount} signal`}
                  </td>
                  <td>
                    {dictionary.publishedRevision == null ? (
                      // A dictionary with no published revision contributes nothing at publication,
                      // and the operator needs to see that before they wonder why. Loose equality on
                      // purpose: the server omits a null member rather than sending null, so this
                      // arrives as undefined and a strict check would print "Revision undefined".
                      <span className="pill sev-3">Draft only</span>
                    ) : (
                      <span className="pill sev-2">Revision {dictionary.publishedRevision}</span>
                    )}
                  </td>
                  <td className="secondary">{dictionary.source}</td>
                  <td className="muted secondary">{formatUtc(dictionary.createdUtc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {list.value && list.value.items.length === 0 && (
          <Empty>{list.loading ? 'Loading…' : 'No dictionaries yet. Import one from the catalog to start.'}</Empty>
        )}
      </div>
    </>
  )
}
